import React, { useState, useEffect, useRef } from 'react';
import {
    Camera,
    ShoppingBag,
    Instagram,
    CalendarDays,
    Upload,
    Link,
    Package,
    ChevronDown,
    ChevronRight,
    Search,
    X,
    Download,
    RefreshCw,
    Loader,
    Image as ImageIcon,
    Type,
    Palette,
    Hash,
    Sparkles,
    Check,
    AlertCircle,
    Maximize2,
    ChevronLeft,
    Wand2,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { getActiveProducts, scanProductUrl } from '../services/productService';
import { generateProductPhoto, savePhotoToLibrary, delay, RATE_LIMIT_CONFIG } from '../services/photoStudioService';
import { loadLibraryItems, deleteLibraryItem, addRefinedVersion, uploadLibraryImage } from '../services/libraryService';
import { buildProductPhotoPrompt, buildProductPhotoReferenceImages } from '../../shared/promptBuilders';
import { SCENE_CATEGORIES, ASPECT_RATIOS } from '../data/photoStudioScenes';
import ConfirmationModal from '../components/ConfirmationModal';
import ImageRefinementPanel from '../components/ImageRefinementPanel';
import MaskDrawingCanvas from '../components/MaskDrawingCanvas';
import ImageCropCanvas from '../components/ImageCropCanvas';
import { useImageRefinementJob, startImageRefinement } from '../hooks/useImageRefinementJob';

const CATEGORY_ICONS = {
    ecommerce: ShoppingBag,
    social: Instagram,
    seasonal: CalendarDays,
};

const PRODUCT_SOURCES = [
    { id: 'brand', label: 'Brand Products', icon: Package },
    { id: 'url', label: 'Scan URL', icon: Link },
    { id: 'upload', label: 'Upload Photos', icon: Upload },
];

const ProductPhotoStudio = () => {
    const { selectedBrand, user: appUser } = useApp();
    const { currentUser } = useAuth();

    // Product selection
    const [productSource, setProductSource] = useState('brand');
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearch, setProductSearch] = useState('');
    const [urlInput, setUrlInput] = useState('');
    const [urlScanning, setUrlScanning] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const fileInputRef = useRef(null);

    // Scene configuration
    const [selectedScene, setSelectedScene] = useState(null);
    const [customPrompt, setCustomPrompt] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({ ecommerce: true, social: false, seasonal: false });
    const [aspectRatio, setAspectRatio] = useState('1:1');

    // Branding options
    const [includeLogo, setIncludeLogo] = useState(true);
    const [includeTextOverlay, setIncludeTextOverlay] = useState(false);
    const [headlineText, setHeadlineText] = useState('');
    const [ctaText, setCtaText] = useState('');
    const [brandColorIntensity, setBrandColorIntensity] = useState('moderate');

    // Generation
    const [variantCount, setVariantCount] = useState(1);
    const [generating, setGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, status: '' });
    const [generatedImages, setGeneratedImages] = useState([]);
    const [generationError, setGenerationError] = useState(null);
    const [sessionHistory, setSessionHistory] = useState([]);
    const abortRef = useRef(false);

    // Product gallery (past + present)
    const [productGallery, setProductGallery] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, item: null });
    const [fullscreenImage, setFullscreenImage] = useState(null); // { url, list, index }

    // Refinement side panel
    const [selectedForRefinement, setSelectedForRefinement] = useState(null); // library item or generated image
    const [isRefining, setIsRefining] = useState(false);
    const [refinementError, setRefinementError] = useState(null);
    const [refinementStatus, setRefinementStatus] = useState(null);
    const [refinementJobId, setRefinementJobId] = useState(null);
    const [refinementContext, setRefinementContext] = useState(null);
    const [refinementSuccess, setRefinementSuccess] = useState(false);
    const { job: refinementJob } = useImageRefinementJob(refinementJobId);

    // Advanced refinement (inpainting, bg removal, crop)
    const [showMaskDrawer, setShowMaskDrawer] = useState(false);
    const [inpaintMode, setInpaintMode] = useState('replace');
    const [isInpainting, setIsInpainting] = useState(false);
    const [inpaintError, setInpaintError] = useState(null);
    const [isRemovingBackground, setIsRemovingBackground] = useState(false);
    const [bgRemovalError, setBgRemovalError] = useState(null);
    const [showCropCanvas, setShowCropCanvas] = useState(false);

    // Load brand products
    useEffect(() => {
        if (selectedBrand?.id) {
            setProductsLoading(true);
            getActiveProducts(selectedBrand.id)
                .then(setProducts)
                .catch(err => console.error('Failed to load products:', err))
                .finally(() => setProductsLoading(false));
        }
    }, [selectedBrand?.id]);

    // Reset product when source changes
    useEffect(() => {
        setSelectedProduct(null);
        setUploadedImages([]);
        setUrlInput('');
        setGenerationError(null);
    }, [productSource]);

    // Load past library images for selected product
    const loadProductGallery = async () => {
        if (!selectedProduct?.name || !currentUser?.uid || !selectedBrand?.id) {
            setProductGallery([]);
            return;
        }
        setGalleryLoading(true);
        try {
            const items = await loadLibraryItems(currentUser.uid, selectedBrand.id, {
                productName: selectedProduct.name,
                type: 'image',
            });
            setProductGallery(items);
        } catch (err) {
            console.error('Failed to load product gallery:', err);
            setProductGallery([]);
        } finally {
            setGalleryLoading(false);
        }
    };

    useEffect(() => {
        loadProductGallery();
    }, [selectedProduct?.name, selectedBrand?.id, currentUser?.uid]);

    const filteredProducts = products.filter(p =>
        p.name?.toLowerCase().includes(productSearch.toLowerCase())
    );

    const hasProductInput = selectedProduct || uploadedImages.length > 0;
    const hasSceneInput = selectedScene || customPrompt.trim();
    const canGenerate = hasProductInput && hasSceneInput && !generating;

    // --- Handlers ---

    const handleScanUrl = async () => {
        if (!urlInput.trim()) return;
        setUrlScanning(true);
        setGenerationError(null);
        try {
            const scanned = await scanProductUrl(urlInput.trim());
            setSelectedProduct({
                ...scanned,
                productUrl: urlInput.trim(),
            });
        } catch (err) {
            setGenerationError(`Failed to scan URL: ${err.message}`);
        } finally {
            setUrlScanning(false);
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const remaining = 5 - uploadedImages.length;
        const toProcess = files.slice(0, remaining);

        toProcess.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setUploadedImages(prev => {
                    if (prev.length >= 5) return prev;
                    return [...prev, { url: ev.target.result, name: file.name }];
                });
            };
            reader.readAsDataURL(file);
        });

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeUploadedImage = (index) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    const toggleCategory = (catId) => {
        setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
    };

    const handleGenerate = async () => {
        if (!canGenerate) return;

        setGenerating(true);
        setGeneratedImages([]);
        setGenerationError(null);
        abortRef.current = false;

        const userId = currentUser?.uid;
        const brandId = selectedBrand?.id;

        try {
            const referenceImages = buildProductPhotoReferenceImages(
                selectedBrand,
                selectedProduct,
                uploadedImages.map(img => img.url),
                { includeLogo }
            );

            for (let i = 0; i < variantCount; i++) {
                if (abortRef.current) break;

                setGenerationProgress({
                    current: i + 1,
                    total: variantCount,
                    status: `Generating variant ${i + 1} of ${variantCount}...`
                });

                if (i > 0) {
                    setGenerationProgress(prev => ({
                        ...prev,
                        status: `Waiting ${RATE_LIMIT_CONFIG.delayBetweenRequests / 1000}s before next variant...`
                    }));
                    await delay(RATE_LIMIT_CONFIG.delayBetweenRequests);
                }

                if (abortRef.current) break;

                setGenerationProgress(prev => ({
                    ...prev,
                    status: `Generating variant ${i + 1} of ${variantCount}...`
                }));

                const prompt = buildProductPhotoPrompt(selectedProduct, selectedBrand, {
                    scene: selectedScene,
                    customPrompt,
                    includeLogo,
                    includeTextOverlay,
                    headlineText,
                    ctaText,
                    brandColorIntensity,
                    variantIndex: i,
                });

                const result = await generateProductPhoto({
                    prompt,
                    referenceImages,
                    aspectRatio,
                    userId,
                    onRetry: (info) => {
                        setGenerationProgress(prev => ({ ...prev, status: info.message }));
                    }
                });

                // Auto-save to library
                const libraryItemId = await savePhotoToLibrary({
                    imageUrl: result.url,
                    prompt,
                    aspectRatio,
                    product: selectedProduct,
                    scene: selectedScene,
                    userId,
                    brandId,
                });

                const imageResult = {
                    url: result.url,
                    aspectRatio,
                    generatedAt: result.generatedAt,
                    libraryItemId,
                    sceneName: selectedScene?.name || 'Custom',
                    productName: selectedProduct?.name || 'Uploaded Product',
                };

                setGeneratedImages(prev => [...prev, imageResult]);
            }

            // Save to session history
            setSessionHistory(prev => [{
                timestamp: new Date().toISOString(),
                sceneName: selectedScene?.name || 'Custom',
                productName: selectedProduct?.name || 'Uploaded Product',
                count: variantCount,
                images: [],  // Will be filled from generatedImages
            }, ...prev]);

        } catch (err) {
            console.error('Generation error:', err);
            setGenerationError(err.message);
        } finally {
            setGenerating(false);
            setGenerationProgress({ current: 0, total: 0, status: '' });
            loadProductGallery();
        }
    };

    const handleCancelGeneration = () => {
        abortRef.current = true;
    };

    const handleDownload = async (imageUrl, filename) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'product-photo.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handleRegenerate = async (index) => {
        if (generating) return;
        setGenerating(true);
        setGenerationError(null);

        const userId = currentUser?.uid;
        const brandId = selectedBrand?.id;

        try {
            const referenceImages = buildProductPhotoReferenceImages(
                selectedBrand,
                selectedProduct,
                uploadedImages.map(img => img.url),
                { includeLogo }
            );

            const prompt = buildProductPhotoPrompt(selectedProduct, selectedBrand, {
                scene: selectedScene,
                customPrompt,
                includeLogo,
                includeTextOverlay,
                headlineText,
                ctaText,
                brandColorIntensity,
                variantIndex: 1,
            });

            setGenerationProgress({ current: 1, total: 1, status: 'Regenerating...' });

            const result = await generateProductPhoto({
                prompt,
                referenceImages,
                aspectRatio,
                userId,
                onRetry: (info) => {
                    setGenerationProgress(prev => ({ ...prev, status: info.message }));
                }
            });

            const libraryItemId = await savePhotoToLibrary({
                imageUrl: result.url,
                prompt,
                aspectRatio,
                product: selectedProduct,
                scene: selectedScene,
                userId,
                brandId,
            });

            setGeneratedImages(prev => prev.map((img, i) =>
                i === index ? {
                    ...img,
                    url: result.url,
                    generatedAt: result.generatedAt,
                    libraryItemId,
                } : img
            ));
        } catch (err) {
            console.error('Regeneration error:', err);
            setGenerationError(err.message);
        } finally {
            setGenerating(false);
            setGenerationProgress({ current: 0, total: 0, status: '' });
            loadProductGallery();
        }
    };

    const handleDeleteGalleryItem = async () => {
        if (!deleteConfirm.item) return;
        try {
            await deleteLibraryItem(deleteConfirm.item.id, currentUser.uid);
            setProductGallery(prev => prev.filter(i => i.id !== deleteConfirm.item.id));
            // Also remove from current session results if it matches
            setGeneratedImages(prev => prev.filter(i => i.libraryItemId !== deleteConfirm.item.id));
        } catch (err) {
            console.error('Failed to delete:', err);
            setGenerationError(`Failed to delete: ${err.message}`);
        }
    };

    // --- Refinement ---

    const handleRefineImage = async (prompt, userReferenceImages = []) => {
        if (!selectedForRefinement || !prompt?.trim() || !currentUser?.uid || !selectedBrand?.id) return;

        const imageUrl = selectedForRefinement.imageUrl || selectedForRefinement.url;
        if (!imageUrl) {
            setRefinementError('No image available to refine');
            return;
        }

        const validRefImages = userReferenceImages.filter(img => img.status === 'valid' && img.url);

        setIsRefining(true);
        setRefinementError(null);
        setRefinementStatus(null);

        try {
            const referenceImages = [{
                url: imageUrl,
                instruction: '[ORIGINAL_IMAGE]'
            }];

            // Add original product photos so the AI maintains product fidelity
            if (selectedProduct?.images?.length > 0) {
                selectedProduct.images.slice(0, 3).forEach((img, idx) => {
                    const imgUrl = img.url || img;
                    if (imgUrl) {
                        referenceImages.push({
                            url: imgUrl,
                            instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                        });
                    }
                });
            } else if (selectedProduct?.imageUrl) {
                referenceImages.push({
                    url: selectedProduct.imageUrl,
                    instruction: `[PRODUCT PHOTO] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                });
            } else if (uploadedImages?.length > 0) {
                uploadedImages.slice(0, 3).forEach((img, idx) => {
                    if (img.url) {
                        referenceImages.push({
                            url: img.url,
                            instruction: `[PRODUCT PHOTO ${idx + 1}] - HIGH-FIDELITY OBJECT. The product in the image MUST match this reference exactly. Maintain exact shape, color, design, and branding.`
                        });
                    }
                });
            }

            // Add brand logo
            const logoUrl = selectedBrand?.logoUrl || selectedBrand?.logo || selectedBrand?.branding?.logoUrl;
            if (logoUrl) {
                referenceImages.push({
                    url: logoUrl,
                    instruction: `[BRAND LOGO] - HIGH-FIDELITY OBJECT. Reproduce this logo EXACTLY as-is. Do not recolor, distort, or modify it in any way.`
                });
            }

            validRefImages.forEach(refImg => {
                referenceImages.push({
                    url: refImg.url,
                    instruction: `[REFERENCE: ${refImg.label}]`
                });
            });

            const refinementPrompt = validRefImages.length > 0
                ? `Edit [ORIGINAL_IMAGE]. Reference images: ${validRefImages.map(img => img.label).join(', ')}.\n\n${prompt.trim()}`
                : `Edit [ORIGINAL_IMAGE].\n\n${prompt.trim()}`;

            const { jobId } = await startImageRefinement({
                userId: currentUser.uid,
                brandId: selectedBrand.id,
                originalImageUrl: imageUrl,
                refinementPrompt,
                referenceImages,
                libraryItemId: selectedForRefinement.id || selectedForRefinement.libraryItemId || null,
            });

            setRefinementJobId(jobId);
            setRefinementContext({
                item: selectedForRefinement,
                prompt: prompt.trim(),
            });

            setRefinementStatus('Generating refined image...');
        } catch (error) {
            console.error(`[${new Date().toISOString()}] Failed to start refinement:`, error);
            setRefinementError(error.message);
            setIsRefining(false);
        }
    };

    // Monitor refinement job completion
    useEffect(() => {
        if (!refinementJob || !refinementContext) return;

        const { status, result, error, progress } = refinementJob;

        if (progress?.message && status === 'processing') {
            setRefinementStatus(progress.message);
        }

        if (status === 'completed' && result?.url) {
            const handleCompletion = async () => {
                try {
                    const { item, prompt } = refinementContext;
                    let finalImageUrl = result.url;

                    if (finalImageUrl.startsWith('data:image')) {
                        finalImageUrl = await uploadLibraryImage(
                            finalImageUrl,
                            currentUser.uid,
                            selectedBrand.id,
                            'image'
                        );
                    }

                    const libraryItemId = item.id || item.libraryItemId;
                    if (libraryItemId) {
                        const updatedItem = await addRefinedVersion(
                            libraryItemId,
                            finalImageUrl,
                            prompt,
                            currentUser.uid
                        );
                        setSelectedForRefinement(updatedItem);
                    }

                    setRefinementError(null);
                    setRefinementStatus(null);
                    setRefinementSuccess(true);
                    setTimeout(() => setRefinementSuccess(false), 3000);
                    loadProductGallery();
                } catch (err) {
                    console.error('Refinement completion error:', err);
                    setRefinementError(err.message);
                    setRefinementStatus(null);
                } finally {
                    setIsRefining(false);
                    setRefinementJobId(null);
                    setRefinementContext(null);
                }
            };
            handleCompletion();
        } else if (status === 'failed') {
            setRefinementError(error || 'Image refinement failed');
            setRefinementStatus(null);
            setIsRefining(false);
            setRefinementJobId(null);
            setRefinementContext(null);
        }
    }, [refinementJob, refinementContext]);

    // --- Advanced refinement handlers ---

    const handleRemoveBackground = async () => {
        if (!selectedForRefinement || !currentUser?.uid || !selectedBrand?.id) return;

        const imageUrl = selectedForRefinement.imageUrl || selectedForRefinement.url;
        if (!imageUrl) {
            setBgRemovalError('No image available to process');
            return;
        }

        setIsRemovingBackground(true);
        setBgRemovalError(null);

        try {
            const response = await fetch('http://localhost:3001/api/remove-background', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageUrl,
                    userId: currentUser.uid,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                let errorMessage = errorData.details || errorData.error || 'Background removal failed';
                if (response.status === 429) {
                    errorMessage = 'Rate limit exceeded. Please wait a minute and try again.';
                } else if (response.status === 402) {
                    errorMessage = errorData.message || 'Insufficient credits for this operation.';
                } else if (response.status === 403) {
                    errorMessage = errorData.message || 'Active subscription required.';
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();
            if (!data.url) throw new Error('No image URL in response');

            const libraryItemId = selectedForRefinement.id || selectedForRefinement.libraryItemId;
            if (libraryItemId) {
                const updatedItem = await addRefinedVersion(
                    libraryItemId,
                    data.url,
                    'Background removed',
                    currentUser.uid,
                );
                setSelectedForRefinement(updatedItem);
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);
            loadProductGallery();
        } catch (error) {
            console.error('Background removal failed:', error);
            setBgRemovalError(error.message || 'Failed to remove background');
        } finally {
            setIsRemovingBackground(false);
        }
    };

    const handleInpaint = async (maskBase64, customPrompt, customMode) => {
        if (!selectedForRefinement || !currentUser?.uid) return;

        const imageUrl = selectedForRefinement.imageUrl || selectedForRefinement.url;
        if (!imageUrl) {
            setInpaintError('No image available to edit');
            return;
        }

        const finalMode = customMode || inpaintMode;

        setShowMaskDrawer(false);
        setIsInpainting(true);
        setInpaintError(null);

        try {
            let response;
            let retries = 0;
            const MAX_RETRIES = 1;

            while (retries <= MAX_RETRIES) {
                response = await fetch('http://localhost:3001/api/inpaint-image', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        imageUrl,
                        maskBase64,
                        prompt: customPrompt || (finalMode === 'remove' ? 'Remove the object in the masked area and replace with the surrounding background' : 'Edit the masked area'),
                        editMode: finalMode,
                        userId: currentUser.uid,
                    }),
                });

                if (response.ok) break;

                const errorData = await response.json().catch(() => ({}));
                const isOverloaded = response.status === 500 && (
                    errorData.details?.toLowerCase().includes('overloaded') ||
                    errorData.error?.toLowerCase().includes('overloaded')
                );

                if (isOverloaded && retries < MAX_RETRIES) {
                    retries++;
                    setInpaintError('Model is overloaded. Automatic retry in 60 seconds...');
                    await new Promise(resolve => setTimeout(resolve, 60000));
                    setInpaintError(null);
                    continue;
                }

                throw new Error(errorData.details || errorData.error || 'Failed to edit image');
            }

            const data = await response.json();
            if (!data.editedImageUrl) throw new Error('No edited image URL in response');

            const libraryItemId = selectedForRefinement.id || selectedForRefinement.libraryItemId;
            const editPrompt = `[${finalMode === 'remove' ? 'Removed' : 'Edited'}] ${customPrompt || 'Masked area'}`;

            if (libraryItemId) {
                const updatedItem = await addRefinedVersion(
                    libraryItemId,
                    data.editedImageUrl,
                    editPrompt,
                    currentUser.uid,
                );
                setSelectedForRefinement(updatedItem);
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);
            loadProductGallery();
        } catch (error) {
            console.error('Inpainting failed:', error);
            setInpaintError(error.message || 'Failed to edit image');
        } finally {
            setIsInpainting(false);
        }
    };

    const handleCropComplete = async (croppedDataUrl) => {
        if (!selectedForRefinement || !currentUser?.uid) return;

        setShowCropCanvas(false);

        try {
            const libraryItemId = selectedForRefinement.id || selectedForRefinement.libraryItemId;
            if (libraryItemId) {
                const updatedItem = await addRefinedVersion(
                    libraryItemId,
                    croppedDataUrl,
                    '[Cropped]',
                    currentUser.uid,
                );
                setSelectedForRefinement(updatedItem);
            }

            setRefinementSuccess(true);
            setTimeout(() => setRefinementSuccess(false), 3000);
            loadProductGallery();
        } catch (error) {
            console.error('Crop failed:', error);
            setRefinementError(error.message || 'Failed to save cropped image');
        }
    };

    // --- Fullscreen viewer ---

    const openFullscreen = (url, list, index) => {
        setFullscreenImage({ url, list, index });
    };

    const navigateFullscreen = (direction) => {
        if (!fullscreenImage?.list) return;
        const { list, index } = fullscreenImage;
        const newIndex = direction === 'prev' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < list.length) {
            setFullscreenImage({ url: list[newIndex], list, index: newIndex });
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!fullscreenImage) return;
            if (e.key === 'Escape') setFullscreenImage(null);
            else if (e.key === 'ArrowLeft') { e.preventDefault(); navigateFullscreen('prev'); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); navigateFullscreen('next'); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [fullscreenImage]);

    // Build a combined URL list for fullscreen navigation
    const allImageUrls = [
        ...generatedImages.map(img => img.url),
        ...productGallery.map(item => item.imageUrl || item.thumbnailUrl),
    ].filter(Boolean);

    // --- Product preview ---

    const productPreviewImage = selectedProduct?.images?.[0]?.url
        || selectedProduct?.imageUrl
        || (uploadedImages.length > 0 ? uploadedImages[0].url : null);

    // --- Render ---

    return (
        <div className="animate-fadeIn" style={{ maxWidth: '1600px' }}>
            {/* Header */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
                <h1 style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                }}>
                    <Camera size={32} style={{ color: 'var(--color-primary)' }} />
                    Product Photo Studio
                </h1>
                <p className="text-secondary" style={{ marginTop: 'var(--space-1)' }}>
                    Generate professional, on-brand product photos with AI
                </p>
            </div>

            <div style={{
                display: 'flex',
                gap: 'var(--space-6)',
                alignItems: 'flex-start',
            }}>
                {/* LEFT PANEL — Configuration */}
                <div style={{
                    width: '420px',
                    minWidth: '420px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)',
                }}>
                    {/* Product Selection */}
                    <div className="card" style={{ padding: 'var(--space-5)' }}>
                        <h3 style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            marginBottom: 'var(--space-3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                        }}>
                            <Package size={18} />
                            Select Product
                        </h3>

                        {/* Source tabs */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-1)',
                            marginBottom: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-lg)',
                            padding: '3px',
                        }}>
                            {PRODUCT_SOURCES.map(source => {
                                const Icon = source.icon;
                                return (
                                    <button
                                        key={source.id}
                                        onClick={() => setProductSource(source.id)}
                                        style={{
                                            flex: 1,
                                            padding: 'var(--space-2) var(--space-2)',
                                            borderRadius: 'var(--radius-md)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: productSource === source.id ? 600 : 400,
                                            background: productSource === source.id ? 'var(--color-surface)' : 'transparent',
                                            color: productSource === source.id ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
                                            boxShadow: productSource === source.id ? 'var(--shadow-sm)' : 'none',
                                            transition: 'all var(--transition-fast)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 'var(--space-1)',
                                        }}
                                    >
                                        <Icon size={14} />
                                        {source.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Brand Products */}
                        {productSource === 'brand' && (
                            <div>
                                <div style={{ position: 'relative', marginBottom: 'var(--space-2)' }}>
                                    <Search size={16} style={{
                                        position: 'absolute',
                                        left: 'var(--space-3)',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: 'var(--color-text-tertiary)',
                                    }} />
                                    <input
                                        className="input-field"
                                        placeholder="Search products..."
                                        value={productSearch}
                                        onChange={e => setProductSearch(e.target.value)}
                                        style={{ paddingLeft: 'var(--space-8)' }}
                                    />
                                </div>
                                {productsLoading ? (
                                    <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                                        <Loader size={20} className="animate-spin" style={{ margin: '0 auto' }} />
                                    </div>
                                ) : filteredProducts.length === 0 ? (
                                    <div style={{
                                        padding: 'var(--space-4)',
                                        textAlign: 'center',
                                        color: 'var(--color-text-tertiary)',
                                        fontSize: 'var(--font-size-sm)',
                                    }}>
                                        {products.length === 0 ? 'No products yet. Add products in the Strategy Hub.' : 'No products match your search.'}
                                    </div>
                                ) : (
                                    <div style={{
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 'var(--space-1)',
                                    }}>
                                        {filteredProducts.map(product => {
                                            const imgUrl = product.images?.[0]?.url || product.imageUrl;
                                            const isSelected = selectedProduct?.id === product.id;
                                            return (
                                                <button
                                                    key={product.id}
                                                    onClick={() => setSelectedProduct(isSelected ? null : product)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-3)',
                                                        padding: 'var(--space-2) var(--space-3)',
                                                        borderRadius: 'var(--radius-md)',
                                                        border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                                        background: isSelected ? 'var(--color-primary-light, rgba(124,58,237,0.08))' : 'var(--color-surface)',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        transition: 'all var(--transition-fast)',
                                                    }}
                                                >
                                                    {imgUrl ? (
                                                        <img
                                                            src={imgUrl}
                                                            alt={product.name}
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                objectFit: 'cover',
                                                                borderRadius: 'var(--radius-sm)',
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: 'var(--radius-sm)',
                                                            background: 'var(--color-bg-tertiary)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}>
                                                            <Package size={16} style={{ color: 'var(--color-text-tertiary)' }} />
                                                        </div>
                                                    )}
                                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                                        <div style={{
                                                            fontSize: 'var(--font-size-sm)',
                                                            fontWeight: 500,
                                                            color: 'var(--color-text-primary)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {product.name}
                                                        </div>
                                                        {product.price && (
                                                            <div style={{
                                                                fontSize: 'var(--font-size-xs)',
                                                                color: 'var(--color-text-tertiary)',
                                                            }}>
                                                                {product.price}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isSelected && <Check size={16} style={{ color: 'var(--color-primary)' }} />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Scan URL */}
                        {productSource === 'url' && (
                            <div>
                                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                    <input
                                        className="input-field"
                                        placeholder="https://example.com/product/..."
                                        value={urlInput}
                                        onChange={e => setUrlInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleScanUrl()}
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleScanUrl}
                                        disabled={!urlInput.trim() || urlScanning}
                                        style={{ whiteSpace: 'nowrap' }}
                                    >
                                        {urlScanning ? <Loader size={16} className="animate-spin" /> : 'Scan'}
                                    </button>
                                </div>
                                {urlScanning && (
                                    <div style={{
                                        marginTop: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)',
                                    }}>
                                        Scanning product page...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Upload Photos */}
                        {productSource === 'upload' && (
                            <div>
                                <div
                                    onClick={() => uploadedImages.length < 5 && fileInputRef.current?.click()}
                                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                    onDrop={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                                        if (files.length > 0) {
                                            handleFileUpload({ target: { files } });
                                        }
                                    }}
                                    style={{
                                        border: '2px dashed var(--color-border)',
                                        borderRadius: 'var(--radius-lg)',
                                        padding: 'var(--space-4)',
                                        textAlign: 'center',
                                        cursor: uploadedImages.length < 5 ? 'pointer' : 'default',
                                        opacity: uploadedImages.length >= 5 ? 0.5 : 1,
                                        transition: 'all var(--transition-fast)',
                                    }}
                                >
                                    <Upload size={24} style={{ margin: '0 auto', color: 'var(--color-text-tertiary)' }} />
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        color: 'var(--color-text-secondary)',
                                        marginTop: 'var(--space-2)',
                                    }}>
                                        {uploadedImages.length >= 5
                                            ? 'Maximum 5 images reached'
                                            : 'Drop images here or click to upload'
                                        }
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)',
                                        marginTop: 'var(--space-1)',
                                    }}>
                                        {uploadedImages.length}/5 images
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                />
                                {uploadedImages.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        gap: 'var(--space-2)',
                                        marginTop: 'var(--space-2)',
                                        flexWrap: 'wrap',
                                    }}>
                                        {uploadedImages.map((img, idx) => (
                                            <div key={idx} style={{ position: 'relative' }}>
                                                <img
                                                    src={img.url}
                                                    alt={img.name}
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        objectFit: 'cover',
                                                        borderRadius: 'var(--radius-sm)',
                                                        border: '1px solid var(--color-border)',
                                                    }}
                                                />
                                                <button
                                                    onClick={() => removeUploadedImage(idx)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '-6px',
                                                        right: '-6px',
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: 'var(--color-error)',
                                                        color: 'white',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: 0,
                                                    }}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Product preview */}
                        {(selectedProduct || uploadedImages.length > 0) && (
                            <div style={{
                                marginTop: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                display: 'flex',
                                gap: 'var(--space-3)',
                                alignItems: 'center',
                            }}>
                                {productPreviewImage && (
                                    <img
                                        src={productPreviewImage}
                                        alt="Product preview"
                                        style={{
                                            width: '48px',
                                            height: '48px',
                                            objectFit: 'cover',
                                            borderRadius: 'var(--radius-sm)',
                                        }}
                                    />
                                )}
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600,
                                        color: 'var(--color-text-primary)',
                                    }}>
                                        {selectedProduct?.name || `${uploadedImages.length} photo${uploadedImages.length > 1 ? 's' : ''} uploaded`}
                                    </div>
                                    {selectedProduct?.description && (
                                        <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)',
                                            marginTop: '2px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {selectedProduct.description}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => {
                                        setSelectedProduct(null);
                                        setUploadedImages([]);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--color-text-tertiary)',
                                        padding: 'var(--space-1)',
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Scene Type */}
                    <div className="card" style={{ padding: 'var(--space-5)' }}>
                        <h3 style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            marginBottom: 'var(--space-3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                        }}>
                            <ImageIcon size={18} />
                            Scene Type
                        </h3>

                        {SCENE_CATEGORIES.map(category => {
                            const CategoryIcon = CATEGORY_ICONS[category.id] || ShoppingBag;
                            const isExpanded = expandedCategories[category.id];
                            return (
                                <div key={category.id} style={{ marginBottom: 'var(--space-2)' }}>
                                    <button
                                        onClick={() => toggleCategory(category.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            width: '100%',
                                            padding: 'var(--space-2)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 600,
                                            color: 'var(--color-text-secondary)',
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        <CategoryIcon size={14} />
                                        {category.name}
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)',
                                            fontWeight: 400,
                                        }}>
                                            ({category.scenes.length})
                                        </span>
                                    </button>

                                    {isExpanded && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 'var(--space-2)',
                                            paddingLeft: 'var(--space-2)',
                                            marginTop: 'var(--space-1)',
                                        }}>
                                            {category.scenes.map(scene => {
                                                const isSelected = selectedScene?.id === scene.id;
                                                return (
                                                    <button
                                                        key={scene.id}
                                                        onClick={() => setSelectedScene(isSelected ? null : scene)}
                                                        style={{
                                                            padding: 'var(--space-2) var(--space-3)',
                                                            borderRadius: 'var(--radius-md)',
                                                            border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                                            background: isSelected ? 'var(--color-primary-light, rgba(124,58,237,0.08))' : 'var(--color-surface)',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            transition: 'all var(--transition-fast)',
                                                        }}
                                                    >
                                                        <div style={{
                                                            fontSize: 'var(--font-size-sm)',
                                                            fontWeight: 500,
                                                            color: isSelected ? 'var(--color-primary)' : 'var(--color-text-primary)',
                                                        }}>
                                                            {scene.name}
                                                        </div>
                                                        <div style={{
                                                            fontSize: 'var(--font-size-xs)',
                                                            color: 'var(--color-text-tertiary)',
                                                            marginTop: '2px',
                                                        }}>
                                                            {scene.description}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Custom prompt */}
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <label style={{
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                display: 'block',
                                marginBottom: 'var(--space-1)',
                            }}>
                                Custom Direction {selectedScene && '(supplements preset)'}
                            </label>
                            <textarea
                                className="input-field"
                                placeholder="e.g., Place the product on a marble countertop with morning sunlight..."
                                value={customPrompt}
                                onChange={e => setCustomPrompt(e.target.value)}
                                rows={3}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div className="card" style={{ padding: 'var(--space-5)' }}>
                        <h3 style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            marginBottom: 'var(--space-3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                        }}>
                            <Hash size={18} />
                            Aspect Ratio
                        </h3>
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            flexWrap: 'wrap',
                        }}>
                            {ASPECT_RATIOS.map(ratio => (
                                <button
                                    key={ratio.value}
                                    onClick={() => setAspectRatio(ratio.value)}
                                    style={{
                                        padding: 'var(--space-2) var(--space-3)',
                                        borderRadius: 'var(--radius-md)',
                                        border: aspectRatio === ratio.value ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                        background: aspectRatio === ratio.value ? 'var(--color-primary-light, rgba(124,58,237,0.08))' : 'var(--color-surface)',
                                        cursor: 'pointer',
                                        transition: 'all var(--transition-fast)',
                                    }}
                                >
                                    <div style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 600,
                                        color: aspectRatio === ratio.value ? 'var(--color-primary)' : 'var(--color-text-primary)',
                                    }}>
                                        {ratio.label}
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)',
                                    }}>
                                        {ratio.description}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Branding Options */}
                    <div className="card" style={{ padding: 'var(--space-5)' }}>
                        <h3 style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            marginBottom: 'var(--space-3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                        }}>
                            <Palette size={18} />
                            Branding
                        </h3>

                        {/* Logo toggle */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--space-3)',
                        }}>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                Include brand logo
                            </span>
                            <button
                                onClick={() => setIncludeLogo(!includeLogo)}
                                style={{
                                    width: '44px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    background: includeLogo ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                                    position: 'relative',
                                    transition: 'background var(--transition-fast)',
                                }}
                            >
                                <div style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    position: 'absolute',
                                    top: '3px',
                                    left: includeLogo ? '23px' : '3px',
                                    transition: 'left var(--transition-fast)',
                                }} />
                            </button>
                        </div>

                        {/* Text overlay toggle */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: includeTextOverlay ? 'var(--space-3)' : 0,
                        }}>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                Text overlay
                            </span>
                            <button
                                onClick={() => setIncludeTextOverlay(!includeTextOverlay)}
                                style={{
                                    width: '44px',
                                    height: '24px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    background: includeTextOverlay ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                                    position: 'relative',
                                    transition: 'background var(--transition-fast)',
                                }}
                            >
                                <div style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: 'white',
                                    position: 'absolute',
                                    top: '3px',
                                    left: includeTextOverlay ? '23px' : '3px',
                                    transition: 'left var(--transition-fast)',
                                }} />
                            </button>
                        </div>

                        {includeTextOverlay && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-3)',
                            }}>
                                <input
                                    className="input-field"
                                    placeholder="Headline text..."
                                    value={headlineText}
                                    onChange={e => setHeadlineText(e.target.value)}
                                />
                                <input
                                    className="input-field"
                                    placeholder="CTA text (e.g., Shop Now)..."
                                    value={ctaText}
                                    onChange={e => setCtaText(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Brand color intensity */}
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <span style={{
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                display: 'block',
                                marginBottom: 'var(--space-2)',
                            }}>
                                Brand color intensity
                            </span>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                {['subtle', 'moderate', 'strong'].map(level => (
                                    <button
                                        key={level}
                                        onClick={() => setBrandColorIntensity(level)}
                                        style={{
                                            flex: 1,
                                            padding: 'var(--space-2)',
                                            borderRadius: 'var(--radius-md)',
                                            border: brandColorIntensity === level ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                            background: brandColorIntensity === level ? 'var(--color-primary-light, rgba(124,58,237,0.08))' : 'var(--color-surface)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: brandColorIntensity === level ? 600 : 400,
                                            color: brandColorIntensity === level ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                                            textTransform: 'capitalize',
                                            transition: 'all var(--transition-fast)',
                                        }}
                                    >
                                        {level}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Variants + Generate */}
                    <div className="card" style={{ padding: 'var(--space-5)' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--space-4)',
                        }}>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                color: 'var(--color-text-primary)',
                            }}>
                                Variants
                            </span>
                            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                                {[1, 2, 3, 4].map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setVariantCount(n)}
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: 'var(--radius-md)',
                                            border: variantCount === n ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                                            background: variantCount === n ? 'var(--color-primary)' : 'var(--color-surface)',
                                            color: variantCount === n ? 'white' : 'var(--color-text-primary)',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'all var(--transition-fast)',
                                        }}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {generating ? (
                            <button
                                className="btn btn-secondary"
                                onClick={handleCancelGeneration}
                                style={{ width: '100%' }}
                            >
                                <X size={18} />
                                Cancel Generation
                            </button>
                        ) : (
                            <button
                                className="btn btn-primary"
                                onClick={handleGenerate}
                                disabled={!canGenerate}
                                style={{
                                    width: '100%',
                                    fontSize: 'var(--font-size-base)',
                                    padding: 'var(--space-3)',
                                }}
                            >
                                <Sparkles size={18} />
                                Generate {variantCount > 1 ? `${variantCount} Variants` : 'Photo'}
                            </button>
                        )}

                        {!hasProductInput && !generating && (
                            <div style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-tertiary)',
                                textAlign: 'center',
                                marginTop: 'var(--space-2)',
                            }}>
                                Select a product to get started
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT PANEL — Results */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Error */}
                    {generationError && (
                        <div style={{
                            padding: 'var(--space-3) var(--space-4)',
                            background: 'var(--color-error-light, rgba(239,68,68,0.08))',
                            border: '1px solid var(--color-error)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            color: 'var(--color-error)',
                            fontSize: 'var(--font-size-sm)',
                        }}>
                            <AlertCircle size={16} />
                            {generationError}
                            <button
                                onClick={() => setGenerationError(null)}
                                style={{
                                    marginLeft: 'auto',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--color-error)',
                                }}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    )}

                    {/* Generation progress */}
                    {generating && (
                        <div className="card" style={{
                            padding: 'var(--space-4)',
                            marginBottom: 'var(--space-4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                        }}>
                            <Loader size={20} className="animate-spin" style={{ color: 'var(--color-primary)' }} />
                            <div>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-text-primary)',
                                }}>
                                    {generationProgress.status}
                                </div>
                                {generationProgress.total > 1 && (
                                    <div style={{
                                        marginTop: 'var(--space-2)',
                                        height: '4px',
                                        borderRadius: '2px',
                                        background: 'var(--color-bg-tertiary)',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            borderRadius: '2px',
                                            background: 'var(--color-primary)',
                                            width: `${(generationProgress.current / generationProgress.total) * 100}%`,
                                            transition: 'width 0.5s ease',
                                        }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Generated images */}
                    {generatedImages.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: generatedImages.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                            gap: 'var(--space-4)',
                            marginBottom: 'var(--space-6)',
                        }}>
                            {generatedImages.map((img, idx) => (
                                <div key={idx} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <div
                                        style={{ position: 'relative', cursor: 'pointer' }}
                                        onClick={() => openFullscreen(img.url, allImageUrls, allImageUrls.indexOf(img.url))}
                                    >
                                        <img
                                            src={img.url}
                                            alt={`Generated variant ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                display: 'block',
                                                borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: 'var(--space-2)',
                                            right: 'var(--space-2)',
                                            background: 'rgba(0,0,0,0.5)',
                                            borderRadius: 'var(--radius-sm)',
                                            padding: '4px',
                                            color: 'white',
                                            opacity: 0.7,
                                            transition: 'opacity var(--transition-fast)',
                                        }}>
                                            <Maximize2 size={14} />
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: 'var(--space-3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                            <span className="badge badge-primary" style={{ fontSize: 'var(--font-size-xs)' }}>
                                                {img.aspectRatio}
                                            </span>
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-tertiary)',
                                            }}>
                                                Saved to Library
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => handleDownload(img.url, `${img.productName}-${img.sceneName}-${idx + 1}.png`)}
                                                title="Download"
                                            >
                                                <Download size={16} />
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => handleRegenerate(idx)}
                                                disabled={generating}
                                                title="Regenerate"
                                            >
                                                <RefreshCw size={16} />
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => setSelectedForRefinement({
                                                    url: img.url,
                                                    imageUrl: img.url,
                                                    id: img.libraryItemId,
                                                    libraryItemId: img.libraryItemId,
                                                    metadata: { prompt: img.prompt },
                                                })}
                                                title="Refine with AI"
                                                style={{ color: 'var(--color-primary)' }}
                                            >
                                                <Wand2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty state */}
                    {!generating && generatedImages.length === 0 && (
                        <div className="card" style={{
                            padding: 'var(--space-12)',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                        }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: 'var(--radius-xl)',
                                background: 'var(--color-bg-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <Camera size={36} style={{ color: 'var(--color-text-tertiary)' }} />
                            </div>
                            <div>
                                <h3 style={{
                                    fontFamily: 'var(--font-display)',
                                    fontSize: 'var(--font-size-lg)',
                                    fontWeight: 600,
                                    color: 'var(--color-text-primary)',
                                    marginBottom: 'var(--space-1)',
                                }}>
                                    Ready to shoot
                                </h3>
                                <p style={{
                                    color: 'var(--color-text-tertiary)',
                                    fontSize: 'var(--font-size-sm)',
                                    maxWidth: '360px',
                                    margin: '0 auto',
                                }}>
                                    Select a product and scene type, then hit Generate to create professional product photos.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Session history */}
                    {sessionHistory.length > 0 && generatedImages.length > 0 && (
                        <div>
                            <h4 style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-2)',
                            }}>
                                Session History
                            </h4>
                            <div style={{
                                display: 'flex',
                                gap: 'var(--space-2)',
                                flexWrap: 'wrap',
                            }}>
                                {sessionHistory.map((entry, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            padding: 'var(--space-2) var(--space-3)',
                                            borderRadius: 'var(--radius-md)',
                                            background: 'var(--color-bg-secondary)',
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--color-text-tertiary)',
                                        }}
                                    >
                                        {entry.productName} — {entry.sceneName} ({entry.count} variant{entry.count > 1 ? 's' : ''})
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Product Photo Gallery — all images for this product */}
                    {selectedProduct?.name && (
                        <div style={{ marginTop: 'var(--space-6)' }}>
                            <h3 style={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 'var(--font-size-lg)',
                                fontWeight: 600,
                                color: 'var(--color-text-primary)',
                                marginBottom: 'var(--space-3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                            }}>
                                <ImageIcon size={18} />
                                All Photos — {selectedProduct.name}
                                {!galleryLoading && (
                                    <span style={{
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 400,
                                        color: 'var(--color-text-tertiary)',
                                    }}>
                                        ({productGallery.length})
                                    </span>
                                )}
                            </h3>

                            {galleryLoading ? (
                                <div style={{
                                    padding: 'var(--space-6)',
                                    textAlign: 'center',
                                    color: 'var(--color-text-tertiary)',
                                }}>
                                    <Loader size={24} className="animate-spin" style={{ margin: '0 auto' }} />
                                    <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                                        Loading product photos...
                                    </div>
                                </div>
                            ) : productGallery.length === 0 ? (
                                <div style={{
                                    padding: 'var(--space-6)',
                                    textAlign: 'center',
                                    color: 'var(--color-text-tertiary)',
                                    fontSize: 'var(--font-size-sm)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                }}>
                                    No photos generated for this product yet.
                                </div>
                            ) : (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 'var(--space-3)',
                                }}>
                                    {productGallery.map(item => {
                                        const imgUrl = item.imageUrl || item.thumbnailUrl;
                                        return (
                                        <div key={item.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                                            <div
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                onClick={() => openFullscreen(imgUrl, allImageUrls, allImageUrls.indexOf(imgUrl))}
                                            >
                                                <img
                                                    src={imgUrl}
                                                    alt={item.name || 'Product photo'}
                                                    style={{
                                                        width: '100%',
                                                        display: 'block',
                                                        borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                                                    }}
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 'var(--space-2)',
                                                    right: 'var(--space-2)',
                                                    background: 'rgba(0,0,0,0.5)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: '4px',
                                                    color: 'white',
                                                    opacity: 0.7,
                                                    transition: 'opacity var(--transition-fast)',
                                                }}>
                                                    <Maximize2 size={14} />
                                                </div>
                                            </div>
                                            <div style={{
                                                padding: 'var(--space-2) var(--space-3)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                            }}>
                                                <div style={{ overflow: 'hidden', flex: 1 }}>
                                                    {item.metadata?.sceneType && (
                                                        <span className="badge badge-neutral" style={{ fontSize: '10px' }}>
                                                            {item.metadata.sceneType}
                                                        </span>
                                                    )}
                                                    {item.metadata?.aspectRatio && (
                                                        <span className="badge badge-primary" style={{ fontSize: '10px', marginLeft: 'var(--space-1)' }}>
                                                            {item.metadata.aspectRatio}
                                                        </span>
                                                    )}
                                                    <div style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        color: 'var(--color-text-tertiary)',
                                                        marginTop: '2px',
                                                    }}>
                                                        {item.createdAt?.toDate
                                                            ? item.createdAt.toDate().toLocaleDateString()
                                                            : item.createdAt
                                                                ? new Date(item.createdAt).toLocaleDateString()
                                                                : ''}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '2px' }}>
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => handleDownload(
                                                            item.imageUrl,
                                                            `${selectedProduct.name}-${item.metadata?.sceneType || 'photo'}.png`
                                                        )}
                                                        title="Download"
                                                    >
                                                        <Download size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => setSelectedForRefinement(item)}
                                                        title="Refine with AI"
                                                        style={{ color: 'var(--color-primary)' }}
                                                    >
                                                        <Wand2 size={14} />
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        onClick={() => setDeleteConfirm({ isOpen: true, item })}
                                                        title="Delete"
                                                        style={{ color: 'var(--color-error)' }}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Refinement Side Panel */}
            {selectedForRefinement && (
                <>
                    {/* Backdrop */}
                    <div
                        onClick={() => {
                            if (!isRefining && !isInpainting && !isRemovingBackground) {
                                setSelectedForRefinement(null);
                                setRefinementError(null);
                                setRefinementStatus(null);
                                setBgRemovalError(null);
                                setInpaintError(null);
                            }
                        }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.4)',
                            zIndex: 9990,
                        }}
                    />
                    {/* Panel */}
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '480px',
                        height: '100vh',
                        background: 'var(--color-surface)',
                        borderLeft: '1px solid var(--color-border)',
                        zIndex: 9991,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-xl)',
                    }}>
                        {/* Panel header */}
                        <div style={{
                            padding: 'var(--space-4) var(--space-5)',
                            borderBottom: '1px solid var(--color-border)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <h3 style={{
                                fontFamily: 'var(--font-display)',
                                fontSize: 'var(--font-size-lg)',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                margin: 0,
                            }}>
                                <Wand2 size={18} style={{ color: 'var(--color-primary)' }} />
                                Refine Image
                            </h3>
                            <button
                                onClick={() => {
                                    if (!isRefining) {
                                        setSelectedForRefinement(null);
                                        setRefinementError(null);
                                    }
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: isRefining ? 'not-allowed' : 'pointer',
                                    color: 'var(--color-text-tertiary)',
                                    padding: 'var(--space-1)',
                                    opacity: isRefining ? 0.5 : 1,
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Panel body - scrollable */}
                        <div style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: 'var(--space-4) var(--space-5)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-4)',
                        }}>
                            {/* Current image preview */}
                            <div style={{
                                borderRadius: 'var(--radius-lg)',
                                overflow: 'hidden',
                                border: '1px solid var(--color-border)',
                                cursor: 'pointer',
                            }}
                                onClick={() => {
                                    const url = selectedForRefinement.imageUrl || selectedForRefinement.url;
                                    if (url) openFullscreen(url, [url], 0);
                                }}
                            >
                                <img
                                    src={selectedForRefinement.imageUrl || selectedForRefinement.url}
                                    alt="Image to refine"
                                    style={{
                                        width: '100%',
                                        display: 'block',
                                    }}
                                />
                            </div>

                            {/* Version history if available */}
                            {selectedForRefinement.history?.length > 1 && (
                                <div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 500,
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)',
                                    }}>
                                        Version History ({selectedForRefinement.history.length})
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        gap: 'var(--space-2)',
                                        overflowX: 'auto',
                                        paddingBottom: 'var(--space-1)',
                                    }}>
                                        {selectedForRefinement.history.map((version, idx) => (
                                            <img
                                                key={idx}
                                                src={version.imageUrl}
                                                alt={version.isOriginal ? 'Original' : `Version ${idx + 1}`}
                                                onClick={() => setSelectedForRefinement(prev => ({
                                                    ...prev,
                                                    imageUrl: version.imageUrl,
                                                }))}
                                                style={{
                                                    width: '56px',
                                                    height: '56px',
                                                    objectFit: 'cover',
                                                    borderRadius: 'var(--radius-sm)',
                                                    cursor: 'pointer',
                                                    border: (selectedForRefinement.imageUrl || selectedForRefinement.url) === version.imageUrl
                                                        ? '2px solid var(--color-primary)'
                                                        : '2px solid var(--color-border)',
                                                    opacity: (selectedForRefinement.imageUrl || selectedForRefinement.url) === version.imageUrl ? 1 : 0.6,
                                                    transition: 'all var(--transition-fast)',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Refinement panel */}
                            <ImageRefinementPanel
                                onRefine={handleRefineImage}
                                isRefining={isRefining}
                                error={refinementError}
                                statusMessage={refinementStatus}
                                userId={currentUser?.uid}
                                brandId={selectedBrand?.id}
                                originalImageUrl={selectedForRefinement.imageUrl || selectedForRefinement.url}
                                originalPrompt={selectedForRefinement.metadata?.prompt}
                                compact={true}
                                onObjectEdit={() => {
                                    setInpaintMode('replace');
                                    setShowMaskDrawer(true);
                                }}
                                onRemoveObject={() => {
                                    setInpaintMode('remove');
                                    setShowMaskDrawer(true);
                                }}
                                onCrop={() => setShowCropCanvas(true)}
                                onRemoveBackground={handleRemoveBackground}
                                isInpainting={isInpainting}
                                isRemovingBackground={isRemovingBackground}
                                bgRemovalError={bgRemovalError}
                                inpaintError={inpaintError}
                            />

                            {/* Success message */}
                            {refinementSuccess && (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--success-50)',
                                    color: 'var(--success-700)',
                                    borderRadius: 'var(--radius-md)',
                                    marginTop: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                }}>
                                    <Sparkles size={16} />
                                    Image refined successfully! Check the version history.
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Mask Drawing Canvas for Object Edit / Remove */}
            {showMaskDrawer && selectedForRefinement && (
                <MaskDrawingCanvas
                    imageUrl={selectedForRefinement.imageUrl || selectedForRefinement.url}
                    onMaskComplete={handleInpaint}
                    onCancel={() => setShowMaskDrawer(false)}
                    initialMode={inpaintMode}
                />
            )}

            {/* Image Crop Canvas */}
            {showCropCanvas && selectedForRefinement && (
                <ImageCropCanvas
                    imageUrl={selectedForRefinement.imageUrl || selectedForRefinement.url}
                    onCropComplete={handleCropComplete}
                    onCancel={() => setShowCropCanvas(false)}
                />
            )}

            {/* Fullscreen Image Viewer */}
            {fullscreenImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.92)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                    onClick={() => setFullscreenImage(null)}
                >
                    {/* Close button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); setFullscreenImage(null); }}
                        style={{
                            position: 'absolute',
                            top: 'var(--space-6)',
                            right: 'var(--space-6)',
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none',
                            borderRadius: 'var(--radius-full)',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                        }}
                    >
                        <X size={20} />
                    </button>

                    {/* Left arrow */}
                    {fullscreenImage.list?.length > 1 && fullscreenImage.index > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); navigateFullscreen('prev'); }}
                            style={{
                                position: 'absolute',
                                left: 'var(--space-6)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                borderRadius: 'var(--radius-full)',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'white',
                            }}
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}

                    {/* Right arrow */}
                    {fullscreenImage.list?.length > 1 && fullscreenImage.index < fullscreenImage.list.length - 1 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); navigateFullscreen('next'); }}
                            style={{
                                position: 'absolute',
                                right: 'var(--space-6)',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                borderRadius: 'var(--radius-full)',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'white',
                            }}
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}

                    {/* Image */}
                    <img
                        src={fullscreenImage.url}
                        alt="Fullscreen preview"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            objectFit: 'contain',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'default',
                        }}
                    />

                    {/* Counter */}
                    {fullscreenImage.list?.length > 1 && (
                        <div style={{
                            position: 'absolute',
                            bottom: 'var(--space-6)',
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: 'var(--font-size-sm)',
                        }}>
                            {fullscreenImage.index + 1} / {fullscreenImage.list.length}
                            <span style={{ marginLeft: 'var(--space-3)' }}>
                                <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '3px', marginRight: '4px' }}>←</kbd>
                                <kbd style={{ background: 'rgba(255,255,255,0.15)', padding: '2px 6px', borderRadius: '3px' }}>→</kbd>
                            </span>
                        </div>
                    )}
                </div>
            )}

            <ConfirmationModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, item: null })}
                onConfirm={handleDeleteGalleryItem}
                title="Delete Photo?"
                message={`Are you sure you want to delete this photo? This will remove it from your library permanently.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default ProductPhotoStudio;
